import { useEffect, useRef, useState, type MouseEvent } from "react";
import { FaXmark } from "react-icons/fa6";

type Props = {
  /** Loading in progress */
  active: boolean;
  /** Media ready — ring eases to 100% then fades out */
  done?: boolean;
  size?: number;
  className?: string;
  /** Cancel download — X in the center (Telegram-style) */
  onCancel?: () => void;
};

/**
 * Telegram-style media loader: arc fills smoothly (fake 60fps) AND whole ring spins.
 * Optional X in the center to cancel before full reveals.
 */
export default function TgMediaProgressRing({
  active,
  done = false,
  size = 44,
  className = "",
  onCancel,
}: Props) {
  const progressRef = useRef(0);
  const angleRef = useRef(0);
  const [paint, setPaint] = useState({ p: 0, a: 0 });
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!active && !done) {
      setVisible(false);
      setFading(false);
      progressRef.current = 0;
      angleRef.current = 0;
      return;
    }

    setVisible(true);
    setFading(false);
    let raf = 0;
    let last = performance.now();
    let finishedAt: number | null = null;

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      angleRef.current = (angleRef.current + 200 * dt) % 360;

      if (done) {
        const remain = 1 - progressRef.current;
        progressRef.current += remain * Math.min(1, dt * 10);
        if (progressRef.current > 0.997) progressRef.current = 1;
        if (progressRef.current >= 1 && finishedAt == null) {
          finishedAt = now;
          setFading(true);
        }
      } else {
        const target = 0.88;
        progressRef.current +=
          (target - progressRef.current) * Math.min(1, dt * 0.45);
        progressRef.current = Math.min(
          target,
          progressRef.current + dt * 0.055,
        );
      }

      setPaint({ p: progressRef.current, a: angleRef.current });

      if (finishedAt != null && now - finishedAt > 220) {
        setVisible(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, done]);

  if (!visible) return null;

  const stroke = 2.75;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const arc = Math.max(0.06, Math.min(0.96, paint.p)) * c;
  const shell = size + 14;
  const canCancel = Boolean(onCancel) && active && !done;

  const handleCancel = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCancel?.();
  };

  return (
    <div
      className={`absolute inset-0 z-[2] grid place-content-center ${canCancel ? "pointer-events-none" : "pointer-events-none"} ${className}`}
      style={{
        opacity: fading ? 0 : 1,
        transition: "opacity 200ms ease",
      }}
    >
      <div
        className="relative grid place-content-center rounded-full bg-black/45"
        style={{ width: shell, height: shell }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="absolute inset-0 m-auto"
          style={{
            transform: `rotate(${paint.a}deg)`,
            willChange: "transform",
          }}
          aria-hidden
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.22)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.95)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${arc} ${c - arc}`}
            strokeDashoffset={0}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>

        {canCancel && (
          <button
            type="button"
            aria-label="Отменить загрузку"
            onClick={handleCancel}
            className="pointer-events-auto relative z-[1] grid place-content-center text-white"
            style={{ width: shell, height: shell }}
          >
            <FaXmark className={size >= 40 ? "h-4 w-4" : "h-3.5 w-3.5"} />
          </button>
        )}
      </div>
    </div>
  );
}
